import type { AccessLevel, ActionKey, ModuleKey } from "@yazamco/shared-types";
import type { IconName } from "../ui/Icon";

export interface NavEntry {
  label: string;
  to: string;
  /** A glyph from the design canvas's icon set — see components/ui/Icon. */
  icon: IconName;
  module?: ModuleKey;
  anyOf?: ModuleKey[];
  /**
   * Shown if the principal holds ANY of these actions, whatever their module
   * levels say.
   *
   * For a screen that is a CONTAINER for surfaces from several modules, where
   * a module level is the wrong question. Settings is the case: its Business
   * Integrations tab is opened by a special action on the `copilot` module, so
   * gating the entry on the `settings` module alone hid the screen from the
   * one permission that was supposed to open it.
   */
  anyAction?: ActionKey[];
  level?: AccessLevel;
}

export interface NavGroup { label?: string; items: NavEntry[]; }

/** The access questions the consumers ask. Satisfied by `useAccess()`. */
export type AtLeast = (module: ModuleKey, level: AccessLevel) => boolean;
export type CanDo = (action: ActionKey) => boolean;

/**
 * Both questions together, passed as one object so a new consumer cannot
 * answer half of them: an entry gated on an action would silently disappear
 * for everyone if a caller supplied only `atLeast`.
 */
export interface NavAccess {
  atLeast: AtLeast;
  can: CanDo;
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: "Tools",
    items: [
      { label: "Microsoft 365 Copilot", to: "/copilot", icon: "sparkle", module: "copilot" },
    ],
  },
  {
    label: "Administration",
    items: [
      {
        // AI agents are no longer their own entry: they are the agent sub-tabs
        // of Copilot admin's Thinking models tab, because both are standing
        // instructions a user picks in chat and are maintained together.
        // The canvas splits administration in two. "Copilot admin" is the
        // product's own configuration — the MCP catalog and the deployment's
        // thinking models — and it is a container: `copilot.mcp_integrations_read`
        // opens the Business Integrations tab inside it and nothing else, so
        // without this the only way to exercise that permission was to hold
        // the unrelated `settings` module as well.
        label: "Copilot admin",
        to: "/settings/copilot",
        icon: "briefcase",
        module: "settings",
        anyAction: ["copilot.mcp_integrations_read"],
      },
      {
        // Platform settings: the deployment's identity, who administers it,
        // and what everyone did. Gated on the `settings` module alone — the
        // per-tab authorities (authorization.read, audit.read) are asked
        // again inside the screen, which shows only the tabs you hold.
        label: "Settings",
        to: "/settings",
        icon: "gear",
        module: "settings",
      },
    ],
  },
];

/**
 * Whether the principal may see this entry. An entry with neither a module nor
 * an action is open. Module levels and actions are OR-ed.
 */
export function navEntryVisible(entry: NavEntry, access: NavAccess): boolean {
  const modules = entry.anyOf ?? (entry.module ? [entry.module] : []);
  const actions = entry.anyAction ?? [];
  if (modules.length === 0 && actions.length === 0) return true;
  return (
    modules.some((module) => access.atLeast(module, entry.level ?? "view")) ||
    actions.some((action) => access.can(action))
  );
}

export function visibleNavGroups(access: NavAccess): NavGroup[] {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((entry) => navEntryVisible(entry, access)),
  })).filter((group) => group.items.length > 0);
}

export function firstPermittedRoute(access: NavAccess): string | null {
  for (const group of NAV_GROUPS) {
    for (const entry of group.items) {
      if (navEntryVisible(entry, access)) return entry.to;
    }
  }
  return null;
}
